import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { PageHeader, StartCTA } from "@/components/site/primitives";
import { LEVELS } from "@/data/buildLevels";

const reveal = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function BuildLevels() {
  return (
    <div>
      <PageHeader
        label="BUILD LEVELS"
        title="Which version of your idea do you need?"
        intro="Different ideas need different builds. From a simple site to a full multi-user product — we help you land on the right level during the assessment."
      />

      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-5">
          {LEVELS.map((l, i) => (
            <motion.div
              key={l.value}
              {...reveal}
              transition={{ ...reveal.transition, delay: (i % 2) * 0.06 }}
              className={`rounded-2xl border p-8 md:p-10 flex flex-col ${
                l.featured ? "border-cyan-400/40 bg-cyan-400/[0.04]"
                : l.isCustom ? "border-[#232323] bg-[#080808] border-dashed"
                : "border-[#161616] bg-[#0a0a0a]"
              }`}
            >
              <div className="flex items-baseline justify-between gap-4">
                <div className={`mono-label ${l.featured ? "text-cyan-300" : "text-white"}`}>{l.name}</div>
                {l.featured && <span className="mono-label text-cyan-400/80 text-[10px]">MOST CHOSEN</span>}
              </div>

              <p className={`mt-5 font-heading tracking-tight leading-snug ${l.isCustom ? "text-2xl md:text-3xl text-zinc-300" : "text-2xl md:text-3xl text-white"}`}>{l.definition}</p>

              <div className="mt-7 pt-6 border-t border-[#161616]">
                <div className="mono-label text-zinc-600 mb-4">{l.isCustom ? "MAY INCLUDE" : "SCOPE"}</div>
                <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
                  {l.examples.map((ex) => (
                    <li key={ex} className="flex items-start gap-2.5">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${l.featured ? "text-cyan-400" : "text-zinc-500"}`} />
                      <span className="text-sm text-zinc-400">{ex}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {l.isCustom && (
                <a
                  href="mailto:Hello@airotech-inc.com?subject=AIROTECH%20Custom%20Build"
                  data-testid="talk-to-airotech"
                  className="group mt-8 self-start inline-flex items-center gap-2 border border-[#2a2a2a] text-white font-medium px-6 py-3.5 rounded-full mono-label hover:border-cyan-400/50 hover:text-cyan-300 transition-colors duration-200"
                >
                  Talk to AIROTECH
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                </a>
              )}
            </motion.div>
          ))}

          <motion.p {...reveal} className="md:col-span-2 pt-4 max-w-2xl text-lg text-zinc-400 leading-relaxed">
            We build far more than MVPs — web applications, PWAs, SaaS platforms, AI applications, interactive
            websites, dashboards and custom software. We'll help you find the right level during the assessment.
          </motion.p>
        </div>
      </section>

      <StartCTA />
    </div>
  );
}
